import React, { useEffect, useState, Fragment } from 'react';
import { motion } from 'framer-motion';
import { Clock, AlertTriangle, ShieldCheck, Lock, CreditCard } from 'lucide-react';
import { Button } from './Button';
interface PricingProps {
  planAOriginal?: string;
  planAPrice?: string;
  planBOriginal?: string;
  planBPrice?: string;
}
export function Pricing({
  planAOriginal = '$30.000',
  planAPrice = '$14.900',
  planBOriginal = '$50.000',
  planBPrice = '$23.800'
}: PricingProps) {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 52,
    seconds: 4
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (num: number) => num.toString().padStart(2, '0');
  return <section id="planes" className="py-20 px-4 bg-sand" data-id="element-152">
      <div className="max-w-5xl mx-auto" data-id="element-153">
        {/* Urgency Block — highlighted */}
        <div className="max-w-2xl mx-auto text-center mb-16" data-id="element-154">
          <div className="relative rounded-3xl border-2 border-amber-400 bg-blue-900 shadow-2xl shadow-blue-900/30 ring-4 ring-amber-400/20 px-5 py-8 md:px-8 md:py-10 mb-8" data-id="element-155">
            <div className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-2.5 rounded-full font-extrabold uppercase tracking-wide text-sm md:text-base mb-7 shadow-lg animate-pulse" data-id="element-156">
              <Clock className="w-5 h-5" data-id="element-157" />⏰ PRECIO DE LANZAMIENTO — CIERRA EN:
            </div>

            <div className="flex justify-center items-center gap-3 md:gap-4 mb-7" data-id="element-158">
              {[{
              label: 'HORAS',
              value: timeLeft.hours
            }, {
              label: 'MIN',
              value: timeLeft.minutes
            }, {
              label: 'SEG',
              value: timeLeft.seconds
            }].map((unit, i) => <Fragment key={i} data-id="element-159">
                  {i > 0 && <span className="text-amber-400 text-4xl md:text-6xl font-black -mt-6" data-id="element-160">
                      :
                    </span>}
                  <div className="flex flex-col items-center" data-id="element-161">
                    <div className="bg-ivory text-blue-900 text-5xl md:text-7xl font-black w-24 h-24 md:w-28 md:h-28 flex items-center justify-center rounded-2xl shadow-xl border-b-4 border-amber-400 tabular-nums" data-id="element-162">
                      {formatTime(unit.value)}
                    </div>
                    <span className="text-amber-300 font-bold text-xs md:text-sm mt-2 tracking-widest" data-id="element-163">
                      {unit.label}
                    </span>
                  </div>
                </Fragment>)}
            </div>

            <p className="text-amber-300 font-extrabold text-lg md:text-2xl flex items-center justify-center gap-2 leading-tight" data-id="element-164">
              <AlertTriangle className="w-6 h-6 md:w-7 md:h-7 flex-shrink-0" data-id="element-165" />
              Después sube $5.000 ARS. No vuelve.
            </p>
          </div>

          <div className="bg-ivory p-4 rounded-xl shadow-sm border border-stone-200 text-left" data-id="element-166">
            <div className="flex justify-between text-sm font-bold text-slate-700 mb-2" data-id="element-167">
              <span className="flex items-center gap-2" data-id="element-168">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" data-id="element-169"></span>{' '}
                Cupos al precio de lanzamiento
              </span>
              <span data-id="element-170">Quedan pocos</span>
            </div>
            <div className="w-full bg-sand rounded-full h-3 mb-2 overflow-hidden" data-id="element-171">
              <motion.div initial={{
              width: 0
            }} whileInView={{
              width: '68%'
            }} transition={{
              duration: 1.5,
              ease: 'easeOut'
            }} className="bg-amber-500 h-3 rounded-full" data-id="element-172" />
            </div>
            <p className="text-xs text-slate-500 text-right font-medium" data-id="element-173">
              68% de los cupos ya tomados
            </p>
          </div>
        </div>

        <h2 className="text-3xl md:text-4xl font-bold text-center text-blue-900 mb-12" data-id="element-174">
          ELEGÍ TU MEGAPACK
        </h2>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-175">
          {/* Card A: Standard */}
          <div className="bg-ivory rounded-3xl p-8 shadow-lg border border-stone-200 flex flex-col h-full" data-id="element-176">
            <div className="mb-6" data-id="element-177">
              <h3 className="text-2xl font-bold text-blue-900 mb-2 flex items-center gap-2" data-id="element-178">
                📘 MEGAPACK MENTE VIVA
              </h3>
              <p className="text-blue-700 font-medium mb-4" data-id="element-179">
                6 Tomos + 6 Bonos
              </p>
              <div className="bg-cream p-4 rounded-xl text-sm text-slate-600 border border-stone-100" data-id="element-180">
                <span className="font-bold text-slate-800" data-id="element-181">Ideal para:</span>{' '}
                Adultos mayores que quieren un sistema completo de estimulación
                cognitiva
              </div>
            </div>

            <div className="mt-auto pt-8" data-id="element-182">
              <div className="text-center mb-6" data-id="element-183">
                <p className="text-slate-400 line-through text-xl font-medium mb-1" data-id="element-184">
                  {planAOriginal}
                </p>
                <p className="text-4xl font-bold text-blue-900" data-id="element-185">
                  {planAPrice}{' '}
                  <span className="text-xl text-slate-500 font-medium" data-id="element-186">
                    ARS
                  </span>
                </p>
              </div>
              <Button href="https://checkout.farixon.com/cart/47861296005172:1" fullWidth className="text-lg" data-id="element-187">
                🧠 → QUIERO MI MEGAPACK MENTE VIVA
              </Button>
            </div>
          </div>

          {/* Card B: Pro (Highlighted) */}
          <div className="bg-blue-900 rounded-3xl p-8 shadow-2xl border-2 border-amber-400 flex flex-col h-full relative transform md:-translate-y-4" data-id="element-188">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber-400 text-blue-950 px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1 shadow-md" data-id="element-189">
              ⭐ MÁS ELEGIDO
            </div>

            <div className="mb-6" data-id="element-190">
              <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2" data-id="element-191">
                🏆 MEGAPACK + PROTOCOLO PRO
              </h3>
              <p className="text-amber-400 font-medium mb-4" data-id="element-192">
                6 Tomos + 6 Bonos + Protocolo Mente Viva Pro
              </p>
              <div className="bg-blue-800/50 p-4 rounded-xl text-sm text-blue-50 border border-blue-700" data-id="element-193">
                <span className="font-bold text-amber-400" data-id="element-194">Ideal para:</span>{' '}
                Familias y cuidadores que quieren medir el progreso y acompañar
                con herramientas reales
              </div>
            </div>

            <div className="mt-auto pt-8" data-id="element-195">
              <div className="text-center mb-6" data-id="element-196">
                <p className="text-blue-300/60 line-through text-xl font-medium mb-1" data-id="element-197">
                  {planBOriginal}
                </p>
                <p className="text-4xl font-bold text-white" data-id="element-198">
                  {planBPrice}{' '}
                  <span className="text-xl text-emerald-200 font-medium" data-id="element-199">
                    ARS
                  </span>
                </p>
              </div>
              <Button href="https://checkout.farixon.com/cart/47866771570740:1" fullWidth variant="primary" className="text-lg shadow-amber-500/20" data-id="element-200">
                🚀 → QUIERO EL MEGAPACK + PROTOCOLO PRO
              </Button>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-600 mt-12 font-medium" data-id="element-201">
          <div className="flex items-center gap-2" data-id="element-202">
            <Lock className="w-5 h-5 text-blue-600" data-id="element-203" /> Pago 100% seguro
          </div>
          <div className="flex items-center gap-2" data-id="element-204">
            <Clock className="w-5 h-5 text-blue-600" data-id="element-205" /> Acceso en 5 minutos
          </div>
          <div className="flex items-center gap-2" data-id="element-206">
            <ShieldCheck className="w-5 h-5 text-blue-600" data-id="element-207" /> Garantía 7 días
          </div>
          <div className="flex items-center gap-2" data-id="element-208">
            <CreditCard className="w-5 h-5 text-blue-600" data-id="element-209" /> Mercado Pago /
            Tarjeta / Transferencia
          </div>
        </div>
      </div>
    </section>;
}